import {
  MdFlashOn,
  MdEditNote,
  MdQrCode2,
  MdLayers,
  MdPhotoSizeSelectLarge,
  MdGrid3X3,
  MdCalculate,
  MdAutoFixHigh,
  MdDraw,
  MdPassword,
  MdPlayCircle,
  MdPictureAsPdf,
  MdArchitecture,
} from 'react-icons/md';
import { isStreamPlayerUnlocked } from '../streamPlayerUnlock';

/** Default Favorite row when `vorakit_favorite_apps` is not set. Order: Focus Lounge, QR Code, App Icon Maker, Text Editor. */
export const DEFAULT_FAVORITE_IDS = [
  'focus-lounge',
  'qr-code',
  'app-icon-maker',
  'text-editor',
];

/** Category order and labels for launcher, footer, and landing. */
export const TOOL_CATEGORIES = [
  { id: 'productivity', label: 'Productivity' },
  { id: 'generators', label: 'Generators' },
  { id: 'document-tools', label: 'Document Tools' },
  { id: 'media-tools', label: 'Media Tools' },
  { id: 'utilities', label: 'Utilities' },
  { id: 'fun', label: 'Fun' },
];

/**
 * Canonical list of VoraKit tools (order within array = display order inside each category).
 * Each item includes `category` matching TOOL_CATEGORIES.id.
 */
export const APP_ITEMS = [
  {
    id: 'focus-lounge',
    category: 'productivity',
    label: 'Focus Lounge',
    path: '/focuslounge',
    iconId: 'zap',
    Icon: MdFlashOn,
  },
  {
    id: 'text-editor',
    category: 'productivity',
    label: 'Text Editor',
    path: '/texteditor',
    iconId: 'editNote',
    Icon: MdEditNote,
  },
  {
    id: 'text-cleaner',
    category: 'productivity',
    label: 'Text Cleaner',
    path: '/text-cleaner',
    iconId: 'autoFix',
    Icon: MdAutoFixHigh,
  },
  {
    id: 'qr-code',
    category: 'generators',
    label: 'QR Code',
    path: '/qrcodegenerator',
    iconId: 'qrCode',
    Icon: MdQrCode2,
  },
  {
    id: 'app-icon-maker',
    category: 'generators',
    label: 'App Icon',
    path: '/appiconmaker',
    iconId: 'layers',
    Icon: MdLayers,
  },
  {
    id: 'password-generator',
    category: 'generators',
    label: 'Password Generator',
    path: '/password-generator',
    iconId: 'password',
    Icon: MdPassword,
  },
  {
    id: 'pdf-studio',
    category: 'document-tools',
    label: 'PDF Studio',
    path: '/pdf-studio',
    iconId: 'pdfStudio',
    Icon: MdPictureAsPdf,
  },
  {
    id: 'image-resizer',
    category: 'media-tools',
    label: 'Image Resizer',
    path: '/imageresizer',
    iconId: 'maximize',
    Icon: MdPhotoSizeSelectLarge,
  },
  {
    id: 'image-marker',
    category: 'media-tools',
    label: 'Image Marker',
    path: '/image-marker',
    iconId: 'draw',
    Icon: MdDraw,
  },
  {
    id: 'stream-player',
    category: 'media-tools',
    label: 'Stream Player',
    path: '/stream-player',
    iconId: 'playCircle',
    Icon: MdPlayCircle,
  },
  {
    id: 'calculator',
    category: 'utilities',
    label: 'Calculator',
    path: '/calculator',
    iconId: 'calculate',
    Icon: MdCalculate,
  },
  {
    id: 'flowspace',
    category: 'utilities',
    label: 'FlowSpace',
    path: '/flowspace',
    iconId: 'architecture',
    Icon: MdArchitecture,
  },
  {
    id: 'tic-tac-toe',
    category: 'fun',
    label: 'Tic Tac Toe',
    path: '/tictactoe',
    iconId: 'grid3',
    Icon: MdGrid3X3,
  },
];

export function getAppByPath(pathname) {
  return APP_ITEMS.find((a) => a.path === pathname) ?? null;
}

/**
 * Tools shown in launcher, footer, and “add favorite” until the user unlocks Stream Player from the navbar logo.
 * @param {boolean} [streamPlayerUnlocked] when omitted, reads {@link isStreamPlayerUnlocked}
 */
export function getVisibleAppItems(streamPlayerUnlocked = isStreamPlayerUnlocked()) {
  if (streamPlayerUnlocked) return APP_ITEMS;
  return APP_ITEMS.filter((a) => a.id !== 'stream-player');
}

/** Tools not currently in Favorite (for All tools section). */
export function getAvailableTools(favoriteIdSet, streamPlayerUnlocked = isStreamPlayerUnlocked()) {
  return getVisibleAppItems(streamPlayerUnlocked).filter((a) => !favoriteIdSet.has(a.id));
}

/** Available tools grouped by category (empty categories omitted). */
export function getAvailableToolsGrouped(favoriteIdSet, streamPlayerUnlocked = isStreamPlayerUnlocked()) {
  const available = getAvailableTools(favoriteIdSet, streamPlayerUnlocked);
  return TOOL_CATEGORIES.map((cat) => ({
    ...cat,
    apps: available.filter((a) => a.category === cat.id),
  })).filter((g) => g.apps.length > 0);
}

/** Footer link groups (all tools, fixed category order). */
export function getFooterToolGroups(streamPlayerUnlocked = isStreamPlayerUnlocked()) {
  const items = getVisibleAppItems(streamPlayerUnlocked);
  return TOOL_CATEGORIES.map((cat) => ({
    title: cat.label,
    items: items.filter((a) => a.category === cat.id).map((a) => ({
      to: a.path,
      label: a.label,
    })),
  }));
}
